import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { Box, CircularProgress, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { ScannerCase, ScannerFileProcessingStatus } from 'models/scanner-case';
import ScannerCaseStatusText from './scanner-status.component';
import ScannerCaseConstructionTypes from './scanner-case-construction-types.component';

type ScannerCaseItemProps = {
    scannerCase: ScannerCase;
    onScannerCaseClick: (caseId: string) => void;
    onDoNotImport: (caseId: string) => void;
    onCreateOrderFromScannerCase: (caseId: string) => void;
};
const ScannerCaseItem: FC<ScannerCaseItemProps> = ({
    scannerCase,
    onScannerCaseClick,
    onDoNotImport,
    onCreateOrderFromScannerCase,
}: ScannerCaseItemProps) => {
    const { t: translate } = useTranslation(['scan-cases', 'orders']);

    const isAwaitingFiles =
        scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.NOT_STARTED ||
        scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.PROCESSING;

    const handleDoNotImportClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation();
        onDoNotImport(scannerCase._id);
    };

    const handleCreateOrderClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation();
        onCreateOrderFromScannerCase(scannerCase._id);
    };

    const handleRowClick = (): void => {
        if (isAwaitingFiles) {
            return;
        }
        onScannerCaseClick(scannerCase._id);
    };

    return (
        <TableRow
            sx={{ cursor: isAwaitingFiles ? 'default' : 'pointer', opacity: isAwaitingFiles ? 0.6 : 1 }}
            hover={!isAwaitingFiles}
            aria-disabled={isAwaitingFiles}
            onClick={handleRowClick}
        >
            <TableCell sx={{ minWidth: 150 }}>
                {isAwaitingFiles ? (
                    <Tooltip title={translate('scan-cases:DOWNLOADING_IN_BACKGROUND').toString()}>
                        <CircularProgress
                            size={16}
                            aria-label={translate('scan-cases:DOWNLOADING_IN_BACKGROUND').toString()}
                        />
                    </Tooltip>
                ) : (
                    <ScannerCaseStatusText status={scannerCase.orderStatus} />
                )}
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>
                <Box>{translate(`scan-cases:${scannerCase.source}`)}</Box>
                <Typography variant="body2" color="grey.700">
                    {scannerCase.externalSourceStatus || <span>{translate('scan-cases:UNKNOWN')}</span>}
                </Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>
                <Box>{scannerCase.dentist?.name}</Box>
                {scannerCase.clinic?.name ? (
                    <Box>
                        <Typography variant="body2" color="grey.700">
                            {scannerCase.clinic?.name}
                        </Typography>
                    </Box>
                ) : undefined}
            </TableCell>
            <TableCell>
                {scannerCase.patient?.firstName ? (
                    <span>{`${scannerCase.patient?.firstName} ${scannerCase.patient?.lastName || ''}`}</span>
                ) : (
                    <span>{translate('scan-cases:NO_PATIENT_NAME')}</span>
                )}
                {scannerCase.patient?.patientRefNo && (
                    <Typography variant="body2" color="grey.700">
                        {scannerCase.patient?.patientRefNo}
                    </Typography>
                )}
            </TableCell>
            <TableCell sx={{ minWidth: 150, maxWidth: 240 }}>
                <ScannerCaseConstructionTypes constructionTypes={scannerCase.constructionTypes} />
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>{formatDate(new Date(scannerCase.updatedAt), 'datetime')}</TableCell>
            <TableCell sx={{ minWidth: 150 }}>
                {scannerCase.deliveryDate ? formatDate(new Date(scannerCase.deliveryDate), 'datetime') : undefined}
            </TableCell>
            <TableCell sx={{ maxWidth: 450 }}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '3em' }}>
                    {scannerCase.comments ? (
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxHeight: '3em' }}>
                            {scannerCase.comments}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="grey.700">
                            {translate('orders:NO_COMMENTS_AVAILABLE')}
                        </Typography>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <Tooltip title={translate('NEW_DENTHUB_ORDER').toString()}>
                        <IconButton disabled={isAwaitingFiles} color="primary" onClick={handleCreateOrderClick}>
                            <AddCircleOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('DO_NOT_IMPORT').toString()}>
                        <IconButton disabled={isAwaitingFiles} onClick={handleDoNotImportClick} color="warning">
                            <DoNotDisturbOnIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </TableCell>
        </TableRow>
    );
};

export default ScannerCaseItem;
